import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { UsageRow, TryonRow } from "@/hooks/useAdminMetrics";

type Bucket = { bucket: string; tryons: number } & Record<string, number | string>;

// Distinct HSL hues for stacked bars (uses semantic primary tints + supporting hues).
const PALETTE = [
  "hsl(var(--primary))",
  "hsl(190 80% 55%)",
  "hsl(280 70% 65%)",
  "hsl(45 90% 60%)",
  "hsl(140 60% 50%)",
  "hsl(0 75% 60%)",
  "hsl(220 70% 60%)",
  "hsl(330 70% 60%)",
];

function bucketKey(d: Date, hourly: boolean): string {
  if (hourly) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}h`;
  }
  return d.toISOString().slice(0, 10);
}

export function SpendChart({
  usage,
  tryons,
  from,
  to,
  rate,
  onCallTypeClick,
}: {
  usage: UsageRow[];
  tryons: TryonRow[];
  from: Date;
  to: Date;
  rate: number;
  onCallTypeClick?: (ct: string) => void;
}) {
  const { data, callTypes } = useMemo(() => {
    const lengthMs = to.getTime() - from.getTime();
    const hourly = lengthMs <= 36 * 60 * 60 * 1000;
    const stepMs = hourly ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

    const buckets = new Map<string, Bucket>();
    const ctSet = new Set<string>();

    // Pre-fill buckets so empty days still show on the X axis.
    const start = new Date(from);
    if (!hourly) start.setHours(0, 0, 0, 0);
    else start.setMinutes(0, 0, 0);
    for (let t = start.getTime(); t <= to.getTime(); t += stepMs) {
      const key = bucketKey(new Date(t), hourly);
      buckets.set(key, { bucket: key, tryons: 0 });
    }

    for (const r of usage) {
      const d = new Date(r.created_at);
      const key = bucketKey(d, hourly);
      const b = buckets.get(key) ?? { bucket: key, tryons: 0 };
      const ct = r.call_type || "unknown";
      ctSet.add(ct);
      b[ct] = ((b[ct] as number) ?? 0) + Number(r.estimated_cost_usd ?? 0);
      buckets.set(key, b);
    }

    for (const t of tryons) {
      const d = new Date(t.created_at);
      const key = bucketKey(d, hourly);
      const b = buckets.get(key) ?? { bucket: key, tryons: 0 };
      b.tryons = (b.tryons as number) + 1;
      buckets.set(key, b);
    }

    return {
      data: Array.from(buckets.values()).sort((a, b) =>
        (a.bucket as string).localeCompare(b.bucket as string),
      ),
      callTypes: Array.from(ctSet).sort(),
    };
  }, [usage, tryons, from, to]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Spend over time</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="cost"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => `$${(v as number).toFixed(2)}`}
              />
              <YAxis
                yAxisId="tryons"
                orientation="right"
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                allowDecimals={false}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const total = payload
                    .filter((p) => p.dataKey !== "tryons")
                    .reduce((s, p) => s + (Number(p.value) || 0), 0);
                  const tryonsEntry = payload.find((p) => p.dataKey === "tryons");
                  return (
                    <div className="rounded-md border bg-popover p-2 text-xs shadow-md">
                      <div className="mb-1 font-mono text-muted-foreground">{label}</div>
                      {payload
                        .filter((p) => p.dataKey !== "tryons" && Number(p.value) > 0)
                        .map((p) => (
                          <div key={String(p.dataKey)} className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-sm"
                              style={{ background: p.color }}
                            />
                            <span className="flex-1">{String(p.dataKey)}</span>
                            <span className="font-medium tabular-nums">
                              ${Number(p.value).toFixed(4)}
                            </span>
                          </div>
                        ))}
                      <div className="mt-1 border-t pt-1 font-medium">
                        Total: ${total.toFixed(4)} · ₹{(total * rate).toFixed(2)}
                      </div>
                      {tryonsEntry ? (
                        <div className="text-muted-foreground">
                          Try-ons: {Number(tryonsEntry.value)}
                        </div>
                      ) : null}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {callTypes.map((ct, i) => (
                <Bar
                  key={ct}
                  yAxisId="cost"
                  dataKey={ct}
                  stackId="cost"
                  fill={PALETTE[i % PALETTE.length]}
                  onClick={() => onCallTypeClick?.(ct)}
                  cursor={onCallTypeClick ? "pointer" : undefined}
                />
              ))}
              <Line
                yAxisId="tryons"
                type="monotone"
                dataKey="tryons"
                stroke="hsl(var(--foreground))"
                strokeWidth={2}
                dot={false}
                name="try-ons"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
